import { useEffect, useState } from 'react';
import { getServicesManager } from '../../services';
import { MetricDetailParamsV6 } from 'interfaces/metrics';
import { DIAL_DEFINITION, CONNECT_DEFINITION, getMetricForStage, StageConfig } from '../../lib/redux/analytics/stage-definitions';
import { BackendTables } from 'interfaces/db';
import { SessionV2Columns, SESSION_METRICS_COLUMNS } from 'interfaces/table';

export interface NumberHealthData {
  phoneNumber: string;
  dials: number;
  connects: number;
  connectRate: number;
}

// Standalone function to fetch metrics
export async function fetchNumberHealthMetrics(phoneNumbers: string[], startDate?: Date, endDate?: Date): Promise<{ data: Map<string, { dials: number; connectRate: number }> }> {
  // Set end to current time
  const end = endDate || new Date();
  // Set start to 14 days ago, at start of day
  const start = startDate || (() => {
    const date = new Date();
    date.setDate(date.getDate() - 14);
    date.setHours(0, 0, 0, 0);
    return date;
  })();

  try {
    const params: MetricDetailParamsV6 = {
      selects: [
        { column_type: 'CNF', cnf: [] }, // Dials (all sessions)
        { column_type: 'CNF', cnf: [[{
          column_name: SESSION_METRICS_COLUMNS.LIVE_DURATION,
          table: BackendTables.SESSION_METRICS,
          operator: 'GT' as any,
          value_safe: 0
        }]] } // Connects (sessions with live_duration > 0)
      ],
      cnf: [[{
        column_name: SessionV2Columns.AGENT_ADDRESS,
        table: BackendTables.SESSIONS_V2,
        operator: 'IN' as any,
        value_safe: phoneNumbers
      }]],
      group_by: {
        column: {
          table: BackendTables.SESSIONS_V2,
          column_name: SessionV2Columns.AGENT_ADDRESS
        }
      },
      start,
      end
    };

    const result = await getServicesManager().getMetricDetailsV6(params);

    const metricsMap = new Map<string, { dials: number; connectRate: number }>();

    if (result && result.length > 0) {
      // First, create a mapping of clean numbers to original phone numbers
      const phoneNumbersClean = new Map<string, string>();
      phoneNumbers.forEach(phone => {
        const clean = phone.replace(/\D/g, '');
        phoneNumbersClean.set(clean, phone);
      });

      for (const row of result) {
        const returnedNumber = row[0] as string;
        const cleanReturned = returnedNumber.replace(/\D/g, '');
        const dials = (row[1] as number) || 0;
        const connects = (row[2] as number) || 0;
        const connectRate = dials > 0 ? (connects / dials) * 100 : 0;

        // Find the matching original phone number
        const originalPhone = phoneNumbersClean.get(cleanReturned);

        if (originalPhone) {
          metricsMap.set(originalPhone, {
            dials,
            connectRate
          });
        }
      }
    }

    return { data: metricsMap };
  } catch (err) {
    console.error('Error fetching number health metrics:', err);
    return { data: new Map() };
  }
}

export interface UserNumberHealthData {
  userId: string;
  userName: string;
  numbers: NumberHealthData[];
  totalDials: number;
  totalConnects: number;
  overallConnectRate: number;
}

export function useNumberHealthMetrics(phoneNumbers: string[] | null, startDate?: Date, endDate?: Date) {
  const [data, setData] = useState<Map<string, NumberHealthData>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Default to last 2 weeks if not provided
  const end = endDate || new Date();
  const start = startDate || new Date(Date.now() - 14 * 24 * 60 * 60 * 1000);

  useEffect(() => {
    if (!phoneNumbers || phoneNumbers.length === 0) {
      setData(new Map());
      return;
    }

    const fetchMetrics = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const stageConfig: StageConfig = {
          options: [DIAL_DEFINITION, CONNECT_DEFINITION],
          cumulative: true
        };

        // Create metrics for dials and connects grouped by phone number
        const params: MetricDetailParamsV6 = {
          selects: [
            { column_type: 'CNF', cnf: [] }, // Dials
            { column_type: 'CNF', cnf: [[{
              column_name: SESSION_METRICS_COLUMNS.LIVE_DURATION,
              table: BackendTables.SESSION_METRICS,
              operator: 'GT' as any,
              value_safe: 0
            }]] } // Connects
          ],
          cnf: [[{
            column_name: SessionV2Columns.AGENT_ADDRESS,
            table: BackendTables.SESSIONS_V2,
            operator: 'IN' as any,
            value_safe: phoneNumbers
          }]],
          group_by: {
            column: {
              table: BackendTables.SESSIONS_V2,
              column_name: SessionV2Columns.AGENT_ADDRESS
            }
          },
          start,
          end
        };

        const result = await getServicesManager().getMetricDetailsV6(params);

        if (result && result.length > 0) {
          const metricsMap = new Map<string, NumberHealthData>();

          // Process results - each row is [phoneNumber, dials, connects]
          for (const row of result) {
            const phoneNumber = row[0] as string;
            const dials = (row[1] as number) || 0;
            const connects = (row[2] as number) || 0;
            const connectRate = dials > 0 ? (connects / dials) * 100 : 0;

            metricsMap.set(phoneNumber, {
              phoneNumber,
              dials,
              connects,
              connectRate
            });
          }

          setData(metricsMap);
        } else {
          setData(new Map());
        }
      } catch (err) {
        console.error('Error fetching number health metrics:', err);
        setError('Failed to load connect rates');
        setData(new Map());
      } finally {
        setIsLoading(false);
      }
    };

    fetchMetrics();
  }, [phoneNumbers?.join(','), start.getTime(), end.getTime()]);

  return { data, isLoading, error };
}

export function useUserNumberHealthMetrics(
  userId: string,
  userName: string,
  phoneNumbers: string[],
  startDate?: Date,
  endDate?: Date
): UserNumberHealthData | null {
  const { data, isLoading } = useNumberHealthMetrics(phoneNumbers.length > 0 ? phoneNumbers : null, startDate, endDate);

  if (isLoading || phoneNumbers.length === 0) {
    return null;
  }

  const numbers: NumberHealthData[] = phoneNumbers.map(phone =>
    data.get(phone) || {
      phoneNumber: phone,
      dials: 0,
      connects: 0,
      connectRate: 0
    }
  );

  const totalDials = numbers.reduce((sum, n) => sum + n.dials, 0);
  const totalConnects = numbers.reduce((sum, n) => sum + n.connects, 0);
  const overallConnectRate = totalDials > 0 ? (totalConnects / totalDials) * 100 : 0;

  return {
    userId,
    userName,
    numbers,
    totalDials,
    totalConnects,
    overallConnectRate
  };
}
